import { DELEGATION_MAX_CLIENT_IDS, type DelegationMaxRole } from '@detect/shared';
import type { DelegationView } from '../../services/delegations';

/** Presentation helpers for Management access (#16). */

export const ROLE_LABELS: Record<DelegationMaxRole, string> = {
  viewer: 'Viewer (read only)',
  agent: 'Agent (acknowledge and resolve alerts)',
  supervisor: 'Supervisor',
  admin: 'Admin (configure monitoring)',
};

/** One line saying what this relationship means for the viewing organization.
 *
 *  A scope's client ids belong to the GRANTOR (the organization being managed). Only a grantor row can name
 *  them from the viewer's own client list; a grantee (the provider) must not look the ids up in its OWN
 *  tenant (a different organization's clients) and must not print another organization's raw ids, so it
 *  is told how many clients the scope covers. */
export function describeDelegation(d: DelegationView, clientNames: ReadonlyMap<string, string> = new Map()): string {
  const other = d.counterpartName ?? d.counterpartTenantId;
  const scope =
    d.clientIds === null
      ? 'all clients'
      : d.side === 'grantee'
        ? `${d.clientIds.length} selected client${d.clientIds.length === 1 ? '' : 's'}`
        : d.clientIds.map((id) => clientNames.get(id) ?? id).join(', ');
  const role = d.maxRole ? ROLE_LABELS[d.maxRole] : null;
  if (d.status === 'active') {
    return d.side === 'grantor'
      ? `${other} manages this organization up to ${role} on ${scope}.`
      : `You manage ${other} up to ${role} on ${scope}.`;
  }
  if (d.status === 'pending') {
    if (d.awaiting === 'you') {
      return d.side === 'grantor'
        ? `${other} asked to manage this organization. You choose what access to give.`
        : `${other} offered you management access (up to ${role} on ${scope}).`;
    }
    return `Waiting for ${other} to respond.`;
  }
  if (d.status === 'revoked') return `Ended${d.revokedBySide ? ` by ${d.revokedBySide === d.side ? 'you' : other}` : ''}.`;
  return 'Declined.';
}

/** The actions available on a row. Mirrors the API: either side can end a live or pending
 *  relationship, only the responder can accept or decline. */
export function availableActions(d: DelegationView): ('accept' | 'decline' | 'revoke' | 'repropose')[] {
  if (d.status === 'pending') return d.awaiting === 'you' ? ['accept', 'decline', 'revoke'] : ['revoke'];
  if (d.status === 'active') return ['revoke'];
  return ['repropose'];
}

export interface Terms {
  maxRole: DelegationMaxRole;
  allClients: boolean;
  clientIds: string[];
}

export const DEFAULT_TERMS: Terms = { maxRole: 'viewer', allClients: true, clientIds: [] };

/** Terms → API shape. `null` (every client) is chosen explicitly; an empty "only these" is refused. */
export function termsToApi(t: Terms): { maxRole: DelegationMaxRole; clientIds: string[] | null } {
  if (!t.allClients && t.clientIds.length === 0) throw new Error('Choose at least one client, or all clients.');
  if (!t.allClients && t.clientIds.length > DELEGATION_MAX_CLIENT_IDS) {
    throw new Error(`Choose up to ${DELEGATION_MAX_CLIENT_IDS} clients, or all clients.`);
  }
  return { maxRole: t.maxRole, clientIds: t.allClients ? null : t.clientIds };
}

/** The selection counter shown beside the client list, e.g. "3 of up to 500 selected". */
export function selectionSummary(count: number): string {
  return `${count} of up to ${DELEGATION_MAX_CLIENT_IDS} selected`;
}

/** Can another client still be ticked? At the limit the unticked boxes are disabled. */
export function canSelectMore(count: number): boolean {
  return count < DELEGATION_MAX_CLIENT_IDS;
}
